import { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import { formatTouchpointClassesForApi, filterTouchpointClassesByAllowedTypes } from "components/modals/creative-modal/creative-modal-utils";

import toast from "components/partials/toast/toast";
import {
  useGetTouchpointContentTypesQuery,
  useClassTypeOptions,
  usePopulationTypeOptions,
} from "hooks/api/dictionary";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";
import {
  TouchpointVersionCloneRequestContentType,
  TouchpointVersionCloneRequestPopulationType,
} from "generated/schemas";
import { useClientCampaignsInModal } from "hooks/use-client-campaigns";
import { useCloneTouchpoint } from "hooks/api/use-touchpoints";
import { getErrorMessage, getFYOptions, getActionTouchpointTypeLabel } from "utilities";
import { getCampaignPaths } from "utilities/app-routes";
import { CampaignAttributes, useCurrentCampaign } from "hooks/api/use-campaigns";
import { OptionType } from "ui/select";
import { getCloneTouchpointSchema } from "components/modals/creative-modal/creative-modal-consts";
import { PaginatedRequestOptions } from "types/pagination";

const CAMPAIGN_REQUEST_OPTIONS: PaginatedRequestOptions = {
  unpaged: true,
  sort: "name",
};

export interface CloneFormData {
  campaignId: string;
  touchpointName: string;
  populationType: string;
  contentType: string;
  applyClientTheme: boolean;
  insightsActivator: boolean;
  folderId: string;
  fiscalYear: number;
  copyCode: string;
  classes?: string[];
}

interface UseCloneTouchpointProps {
  isOpen: boolean;
  onClose: () => void;
  selectedVersion?: TouchpointVersionAttributes;
  onSuccess?: () => void;
}

export const useCloneTouchpointModal = ({
  isOpen,
  onClose: onCloseProp,
  selectedVersion,
  onSuccess,
}: UseCloneTouchpointProps) => {
  const navigate = useNavigate();
  const currentClient = useCurrentClient();
  const defaultCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();

  const [selectedCampaign, setSelectedCampaign] = useState<CampaignAttributes>(defaultCampaign);

  const cloneMutation = useCloneTouchpoint();

  const clientCampaigns = useClientCampaignsInModal(isOpen, CAMPAIGN_REQUEST_OPTIONS);

  const campaignsOptions = useMemo(() => {
    return clientCampaigns.map((campaign) => ({
      label: campaign.name,
      value: campaign.id,
    }));
  }, [clientCampaigns]);

  const { data: contentTypes } = useGetTouchpointContentTypesQuery(
    selectedCampaign.campaignObjective,
  );

  const { classTypes, classTypeOptions } = useClassTypeOptions(selectedCampaign.campaignObjective);
  const { populationTypeOptions } = usePopulationTypeOptions();

  const contentTypeOptions = useMemo(() => {
    if (!contentTypes) return [];
    return contentTypes.map((type) => ({
      label: type.description,
      value: type.name,
    }));
  }, [contentTypes]);

  const defaultTouchpointName = `Copy of ${selectedVersion?.name ?? currentTouchpoint.name}`;

  const defaultValues = useMemo(
    () => ({
      campaignId: defaultCampaign.id,
      touchpointName: defaultTouchpointName,
      populationType: currentTouchpoint.populationType ?? "",
      contentType: "",
      applyClientTheme: selectedVersion?.applyClientTheme ?? true,
      folderId: currentTouchpoint.folderId ?? "",
      insightsActivator: currentTouchpoint.insightsActivator,
      classes: currentTouchpoint.classes ?? [],
    }),
    [
      defaultCampaign.id,
      defaultTouchpointName,
      selectedVersion?.applyClientTheme,
      currentTouchpoint.folderId,
      currentTouchpoint.insightsActivator,
      currentTouchpoint.populationType,
      currentTouchpoint.classes,
    ],
  );

  const methods = useForm<CloneFormData>({
    mode: "onChange",
    defaultValues,
    resolver: getCloneTouchpointSchema(currentTouchpoint.type),
  });

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { errors, isValid, isSubmitted },
  } = methods;

  const inputRef = useRef<HTMLInputElement>(null);
  const prevIsOpen = useRef(isOpen);
  const fyOptions = getFYOptions();

  useEffect(() => {
    if (!prevIsOpen.current && isOpen) {
      reset(defaultValues);
    } else if (prevIsOpen.current && !isOpen) {
      setSelectedCampaign(defaultCampaign);
      reset(defaultValues);
    }
    prevIsOpen.current = isOpen;
  }, [defaultCampaign.id, defaultValues, isOpen, reset, defaultCampaign]);

  useEffect(() => {
    if (!isOpen) return;
    if (selectedCampaign?.id === defaultCampaign?.id) {
      setValue("folderId", currentTouchpoint.folderId ?? "", { shouldValidate: true });
      return;
    }
    setValue("folderId", "", { shouldValidate: true });
  }, [defaultCampaign?.id, isOpen, selectedCampaign?.id, setValue, currentTouchpoint.folderId]);

  useEffect(() => {
    if (!isOpen || !contentTypes) return;

    if (contentTypes.length === 1) {
      setValue("contentType", contentTypes[0].name, { shouldValidate: true });
      return;
    }

    const currentValue = getValues("contentType");
    const isCurrentValueAllowed = contentTypes.some((type) => type.name === currentValue);

    if (!isCurrentValueAllowed) {
      setValue("contentType", "", { shouldValidate: false });
    }
  }, [isOpen, contentTypes, getValues, setValue, selectedCampaign?.id]);

  useEffect(() => {
    if (!isOpen || classTypes.length === 0) return;

    const filteredClasses = filterTouchpointClassesByAllowedTypes(getValues("classes"), classTypes);
    setValue("classes", filteredClasses, { shouldValidate: true });
  }, [classTypes, getValues, isOpen, selectedCampaign?.id, setValue]);

  const handleClose = useCallback(() => {
    if (cloneMutation.isPending) return;
    onCloseProp();
  }, [cloneMutation.isPending, onCloseProp]);

  const handleSubmitCloneTouchpoint = useCallback(
    (data: CloneFormData) => {
      if (!selectedVersion) return;
      if (cloneMutation.isPending) return;

      cloneMutation
        .mutateAsync({
          touchpointId: currentTouchpoint.id,
          versionId: selectedVersion.id,
          data: {
            campaignId: data.campaignId,
            name: data.touchpointName,
            contentType: data.contentType as TouchpointVersionCloneRequestContentType,
            populationType: data.populationType as TouchpointVersionCloneRequestPopulationType,
            applyClientTheme: data.applyClientTheme,
            folderId: data.folderId,
            fiscalYear: data.fiscalYear,
            copyCode: data.copyCode,
            insightsActivator: data.insightsActivator,
            classes: formatTouchpointClassesForApi(data.classes),
          },
        })
        .then(() => {
          const selectedCampaign = clientCampaigns.find(
            (campaign) => campaign.id === data.campaignId,
          );

          toast.success({
            data: {
              title: `Cloned ${getActionTouchpointTypeLabel(currentTouchpoint.type).toLowerCase()} to ${selectedCampaign?.name}`,
            },
          });

          if (onSuccess && data.campaignId === defaultCampaign.id) {
            onSuccess();
          } else {
            const { campaignCreativePath } = getCampaignPaths({
              clientId: currentClient.id,
              campaignId: data.campaignId,
            });

            navigate(campaignCreativePath);
          }

          onCloseProp();
        })
        .catch((err) => {
          toast.error({
            data: {
              title: `Failed to clone ${getActionTouchpointTypeLabel(currentTouchpoint.type).toLowerCase()}`,
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    },
    [
      clientCampaigns,
      currentClient.id,
      currentTouchpoint.id,
      navigate,
      onCloseProp,
      selectedVersion,
      onSuccess,
      defaultCampaign.id,
      cloneMutation,
    ],
  );

  const onCampaignChange = (
    selectedOption: OptionType | null,
    fieldOnChange: (value: string) => void,
  ) => {
    const campaign =
      clientCampaigns.find((campaign) => campaign.id === selectedOption?.value) ?? defaultCampaign;

    setSelectedCampaign(campaign);
    fieldOnChange(campaign.id);
  };

  return {
    methods,
    control,
    handleSubmit,
    errors,
    isValid,
    isPending: cloneMutation.isPending,
    campaignsOptions,
    contentTypeOptions,
    populationTypeOptions,
    fyOptions,
    currentClient,
    selectedCampaign,
    currentTouchpoint,
    inputRef,
    handleClose,
    handleSubmitCloneTouchpoint,
    onCampaignChange,
    contentTypes,
    classTypeOptions,
  };
};
